import * as Sentry from '@sentry/react';
import { SonnerToaster, TooltipProvider } from '@sr/ui-library';
import { Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { prefetchFeatures } from './api/prefetch';
import { AppLoader } from './components/Loader';
import { useAnalyticsIdentity } from './hooks/useAnalyticsIdentity';
import { useAuth } from './hooks/useAuth';
import { useCurrentUserAuth } from './hooks/useCurrentUserAuth';
import { QueryProvider } from './providers/QueryProvider';
import { userService } from './reducers/permissions/userSlice';
import { reduxServices } from './reducers/reduxServices';
import { AppRouter } from './routes';
import { useAppDispatch } from './stateStore';

const App = () => {
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth();
  const { currentUser } = useCurrentUserAuth();

  // Covers both the Cognito and Auth0 provider paths -- App is the shared
  // top of the tree above the router, so this runs once for every
  // authenticated route (APP-2232).
  useAnalyticsIdentity();

  useEffect(() => {
    const fetchUserMe = async () => {
      reduxServices.globalState.getAccessTokenSilently = getAccessTokenSilently;
      dispatch(userService.findMe());
      dispatch(reduxServices.setting.findAllFeatures());
      dispatch(reduxServices.setting.findAllConfiguration());

      // Prefetch feature flags using Tanstack Query
      prefetchFeatures();
    };

    if (isAuthenticated) fetchUserMe();
  }, [dispatch, getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!currentUser) return;

    // Ensure current user id is included with Sentry error reports
    const scope = Sentry.getCurrentScope();

    scope.setUser({
      id: currentUser?._id,
    });
  }, [currentUser]);

  if (isLoading)
    return <AppLoader.FlexLoader loaderHeight="h-screen" size="8" />;

  return (
    <QueryProvider>
      <TooltipProvider>
        <Suspense
          fallback={<AppLoader.FlexLoader className="h-screen" size="8" />}
        >
          <RouterProvider router={AppRouter} />
          <SonnerToaster duration={7000} position="top-center" />
        </Suspense>
      </TooltipProvider>
    </QueryProvider>
  );
};

export default App;
